import type { Transform } from "./geometry";
import type { ComponentOfType, ComponentType, EntityComponent } from "./components";

export type EntityId = string;
export type EntityKind =
  | "character"
  | "furniture"
  | "decoration"
  | "artifact"
  | "clothing"
  | "portal"
  | "prop";

export interface Entity {
  readonly id: EntityId;
  readonly kind: EntityKind;
  readonly definitionId: string;
  readonly transform: Transform;
  readonly components: readonly EntityComponent[];
  readonly visible: boolean;
}

export const PLAYER_CHARACTER_ID: EntityId = "kuki";

export function getComponent<T extends ComponentType>(
  entity: Entity,
  type: T,
): ComponentOfType<T> | undefined {
  return entity.components.find((c): c is ComponentOfType<T> => c.type === type);
}

export function hasComponent(entity: Entity, type: ComponentType): boolean {
  return entity.components.some((c) => c.type === type);
}

export function withComponent<T extends ComponentType>(
  entity: Entity,
  type: T,
  update: (current: ComponentOfType<T>) => ComponentOfType<T>,
): Entity {
  let found = false;
  const components = entity.components.map((c) => {
    if (c.type === type) {
      found = true;
      return update(c as ComponentOfType<T>);
    }
    return c;
  });
  if (!found) {
    throw new Error(`Entity ${entity.id} has no component of type "${type}"`);
  }
  return { ...entity, components };
}

export function withTransform(entity: Entity, transform: Partial<Transform>): Entity {
  return { ...entity, transform: { ...entity.transform, ...transform } };
}
